import React, { useState, useEffect, Fragment } from "react";
import Container from "../../../../../widgets/Container";
import StudentGetSearchView from "./attendance/StudentGetSearchView";
import { useDispatch } from "react-redux";
import { pageTitle } from "../../../../../store/softSlice";
import AppURL from "../../../../../widgets/AppURL";
import { postDataToTheServer } from "../../../../../widgets/api";
import { ToastSuccess } from "../../../../../widgets/ToastMessage";

const SubjStudentAttendanceCreateView = ({ setIconsButton, PageTitle }) => {
  const teacher_id = sessionStorage.getItem("teacherAuth");
  const dispatch = useDispatch();
  const [onStudentList, setOnStudentList] = useState([]);
  const [onSelectedDays, setOnSelectedDays] = useState("");
  const [onShow, setOnShow] = useState(true);

  useEffect(() => {
    dispatch(pageTitle({ title: PageTitle }));
    document.title = `${PageTitle}`;
    setIconsButton(<button onClick={() => setOnShow(!onShow)}>
      {onShow ? "Hide" : "Show"}
    </button>);
  }, [onShow, setIconsButton]);

  // Attendance status options
  const attendanceStatuses = {
    PRESENT: "Present",
    LATE: "Late",
    LEAVE: "Leave",
    HALF: "Half-day",
    ABSENT: "Absent",
  };

  // Default status
  const DEFAULT_STATUS = "Present";

  // Transform the student data to match expected format
  const transformStudentData = (students) => {
    return students?.map((student, index) => ({
      id: student.member_sn || index + 1,
      name: `${student.first_name} ${student.last_name}`,
      rollNumber: student.rollNumber || `ROLL${index + 1}`,
      program: student.program || "N/A",
      year_semester: student.year_semester || "N/A",
      acdmc_year: student.acdmc_year || "N/A",
      section: student.section || "N/A",
      status: student.status || 0, // Assuming 0 is "Not Available",
      subject: student.subject || "N/A",
      date: onSelectedDays || "",
      attendance: {
        [onSelectedDays]: student.attendance_status || DEFAULT_STATUS,
      },
      rawData: student, // Keep original data if needed
    }));
  };

  // Load data from student list
  const loadData = () => {
    return {
      students: transformStudentData(onStudentList.results) || [],
      dates: onSelectedDays ? [onSelectedDays] : [],
    };
  };

  const [state, setState] = useState(loadData());
  const [sortConfig, setSortConfig] = useState({
    key: "rollNumber",
    direction: "asc",
  });
  const [searchTerm, setSearchTerm] = useState("");

  // Update state when student list or selected days change
  useEffect(() => {
    setState(loadData());
  }, [onStudentList, onSelectedDays]);

  // Save to localStorage whenever data changes
  const updateAttendanceStatus = (studentId, date, status) => {
    setState((prev) => ({
      ...prev,
      students: prev.students.map((student) => {
        if (student.id === studentId) {
          const newAttendance = {
            ...student.attendance,
            [date]: status,
          };
          return { ...student, attendance: newAttendance };
        }
        return student;
      }),
    }));
  };

  const calculateAttendancePercentage = (student) => {
    const attendedDays = Object.values(student.attendance).filter(
      (status) =>
        status === attendanceStatuses.PRESENT ||
        status === attendanceStatuses.HALF
    ).length;
    const halfDays = Object.values(student.attendance).filter(
      (status) => status === attendanceStatuses.HALF
    ).length;

    // Count half-days as 0.5 attendance
    const totalAttendance = attendedDays - halfDays * 0.5;

    return state.dates.length > 0
      ? Math.round((totalAttendance / state.dates.length) * 100)
      : 0;
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedStudents = React.useMemo(() => {
    let sortableStudents = [...state.students];
    if (searchTerm) {
      sortableStudents = sortableStudents.filter(
        (student) =>
          student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.rollNumber
            .toString()
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    if (sortConfig.key === "attendance") {
      sortableStudents.sort((a, b) => {
        const aPercentage = calculateAttendancePercentage(a);
        const bPercentage = calculateAttendancePercentage(b);
        return sortConfig.direction === "asc"
          ? aPercentage - bPercentage
          : bPercentage - aPercentage;
      });
    } else {
      sortableStudents.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableStudents;
  }, [state.students, sortConfig, searchTerm]);

  const getStatusColor = (status) => {
    switch (status) {
      case attendanceStatuses.PRESENT:
        return "border-green-300";
      case attendanceStatuses.LATE:
        return "border-yellow-300";
      case attendanceStatuses.LEAVE:
        return "border-blue-300";
      case attendanceStatuses.HALF:
        return "border-orange-300";
      case attendanceStatuses.ABSENT:
        return "border-red-300";
      default:
        return "border-gray-300";
    }
  };
  // Add this near your other state declarations
  const [isSaving, setIsSaving] = useState(false);

  // Add this function to handle saving attendance
  const saveAttendanceData = async () => {
    if (!onSelectedDays) {
      return;
    }

    setIsSaving(true);

    try {
      // Prepare the data to send
      const attendanceData = {
        date: onSelectedDays,
        records: state.students.map((student) => ({
          studentId: student.id,
          status: student.attendance[onSelectedDays] || DEFAULT_STATUS,
          rollNumber: student.rollNumber,
          name: student.name,
          program: student.program,
          year_semester: student.year_semester,
          acdmc_year: student.acdmc_year,
          section: student.section,
          subject: student.subject,
          date: student.date || onSelectedDays,
        })),
      };
      const formData = new FormData();
      formData.append("date", onSelectedDays);
      formData.append("teacher_id", teacher_id);
      formData.append("attendance_data", JSON.stringify(attendanceData));
      await postDataToTheServer(
        AppURL.ExamURL + "exam-student-attendance-create-view/",
        formData
      ).then((res) => {
        ToastSuccess(res?.message || "Attendance saved successfully");
      });
    } catch (error) {
      console.log(error.message);
    } finally {
      setIsSaving(false);
    }
  };
  return (
    <Fragment>
      {
        onShow &&  <StudentGetSearchView
        setOnStudentList={setOnStudentList}
        setOnSelectedDays={setOnSelectedDays}
      />
      }
     
      <div className="mt-2">
        <Container>
          <div className="flex flex-wrap gap-4 mb-3">
            <input
              type="text"
              placeholder="Search students..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-2 py-1 border border-slate300 font-public-sans rounded-sm focus:outline-none flex-grow max-w-md"
            />
          </div>
          {onSelectedDays && (
            <div className="mb-4 p-2 bg-blue-50 border font-public-sans text-[12px] border-blue-200 rounded">
              <span className="font-light">Selected Date: </span>
              <strong>{onSelectedDays}</strong>
            </div>
          )}
          <div className="overflow-x-auto h-[60vh] overflow-y-auto">
            <table className="min-w-full bg-white border rounded-lg overflow-hidden font-public-sans">
              <thead>
                <tr className="z-50 sticky top-0">
                  <th
                    onClick={() => handleSort("id")}
                    className="px-2 py-2 bg-slate100 text-left text-[12px] font-medium text-slate800 tracking-wider cursor-pointer"
                  >
                    ID{" "}
                    {sortConfig.key === "id" &&
                      (sortConfig.direction === "asc" ? "↑" : "↓")}
                  </th>
                  <th
                    onClick={() => handleSort("name")}
                    className="px-2 py-2 bg-slate100 text-left text-[12px] font-medium text-slate800 tracking-wider cursor-pointer"
                  >
                    Name{" "}
                    {sortConfig.key === "name" &&
                      (sortConfig.direction === "asc" ? "↑" : "↓")}
                  </th>
                  <th
                    onClick={() => handleSort("rollNumber")}
                    className="px-2 py-2 bg-slate100 text-left text-[12px] font-medium text-slate800 tracking-wider cursor-pointer"
                  >
                    Roll No.{" "}
                    {sortConfig.key === "rollNumber" &&
                      (sortConfig.direction === "asc" ? "↑" : "↓")}
                  </th>
                  {onSelectedDays && (
                    <th className="px-2 bg-slate100 py-1 text-left text-[12px] font-medium text-slate800 uppercase tracking-wider">
                      Status
                    </th>
                  )}
                  <th
                    onClick={() => handleSort("attendance")}
                    className="px-2 py-2 bg-slate100 text-left text-[12px] font-medium text-slate800 tracking-wider cursor-pointer"
                  >
                    Attendance %{" "}
                    {sortConfig.key === "attendance" &&
                      (sortConfig.direction === "asc" ? "↑" : "↓")}
                  </th>
                  <th
                    onClick={() => handleSort("attendance")}
                    className="px-2 py-2 bg-slate100 text-left text-[12px] font-medium text-slate800 tracking-wider cursor-pointer"
                  >
                    Attendance Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedStudents.length > 0 ? (
                  sortedStudents.map((student) => (
                    <tr
                      key={student.id}
                      className="hover:bg-gray-50 text-[13px]"
                    >
                      <td className="px-2 py-1 whitespace-nowrap">
                        {student.id}
                      </td>
                      <td className="px-2 py-1 whitespace-nowrap">
                        {student.name}
                      </td>
                      <td className="px-2 py-1 whitespace-nowrap">
                        {student.rollNumber}
                      </td>
                      {onSelectedDays && (
                        <td className="px-2 py-1 whitespace-nowrap">
                          <div className="flex gap-4 flex-wrap">
                            {Object.entries(attendanceStatuses).map(
                              ([key, status]) => (
                                <button
                                  key={key}
                                  onClick={() =>
                                    updateAttendanceStatus(
                                      student.id,
                                      onSelectedDays,
                                      status
                                    )
                                  }
                                  className={`px-6 py-1 rounded-md border-2 font-public-sans text-[12px] font-medium ${getStatusColor(
                                    status
                                  )} ${
                                    student.attendance[onSelectedDays] ===
                                    status
                                      ? "ring-2 ring-offset-1 ring-green-400 bg-green-500 text-white font-bold"
                                      : ""
                                  }`}
                                >
                                  {status}
                                </button>
                              )
                            )}
                          </div>
                        </td>
                      )}
                      <td
                        className={`px-2 py-1 whitespace-nowrap ${
                          calculateAttendancePercentage(student) < 75
                            ? "text-red-600 font-bold"
                            : ""
                        }`}
                      >
                        {calculateAttendancePercentage(student)}%
                      </td>
                      <td className="px-2 py-1 whitespace-nowrap">
                        {student.status === 0
                          ? "Not Available"
                          : student.status === 1
                          ? "Available"
                          : "Not Available"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-2 py-4 text-center text-gray-500"
                    >
                      {onStudentList.length === 0
                        ? "No students found"
                        : "Select a date to view attendance"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-4 pt-3 justify-end items-center">
            <button
              onClick={saveAttendanceData}
              disabled={isSaving || !onSelectedDays}
              className="px-6 py-1.5 bg-green-600 text-white rounded-sm text-[12px] font-public-sans hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-green-300 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Saving...
                </>
              ) : (
                "Save Attendance"
              )}
            </button>
          </div>
        </Container>
      </div>
    </Fragment>
  );
};

export default SubjStudentAttendanceCreateView;
